import {
	mapPartRevisionBomTreeData,
	PartRevisionBomTreeItemModel
} from "src/components/views/erp/parts/treeViews/treeItemMapping.ts";
import React, { useMemo, useState } from "react";
import { alpha, styled, useTheme } from "@mui/material/styles";
import { TreeItem, TreeItemProps } from "@mui/x-tree-view/TreeItem";
import { useTreeItem } from "@mui/x-tree-view/useTreeItem";
import { faAngleDown, faAngleRight } from "@fortawesome/pro-regular-svg-icons";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Typography } from "@mui/material";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import i18n from "i18next";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import { AavoTreeView } from "src/components/common/tree/AavoTreeView.tsx";
import { flatMapTreeItems } from "src/components/common/tree/AavoTreeView.utils.ts";
import { AavoCheckbox } from "src/components/common/inputFields/AavoCheckbox.tsx";
import { PartTreeElement } from "src/api/generated/erp/parts/treeViews/model/partTreeElement.ts";

export interface PartRevisionBomTreeViewBaseProps {
	treeElements: PartTreeElement[];
	showRevisions?: boolean;
	onSearchQueryChange: (searchQuery: string) => void;
	onShowRevisionsChange?: (showRevisions: boolean) => void;
	onSelectionChanged?: (selectedItem: PartRevisionBomTreeItemModel | null) => void;
}

export const PartRevisionBomTreeViewBase = ({
												treeElements,
												showRevisions,
												onSearchQueryChange,
												onShowRevisionsChange,
												onSelectionChanged
											}: PartRevisionBomTreeViewBaseProps) => {
	const [selectedItem, setSelectedItemLocal] = useState<PartRevisionBomTreeItemModel | null>(null);

	const rootItems: PartRevisionBomTreeItemModel[] = useMemo(
		() => mapPartRevisionBomTreeData(treeElements),
		[treeElements]
	);
	const flattenItems = useMemo(() => flatMapTreeItems(rootItems), [rootItems]);
	const allItemsByIds: Record<string, PartRevisionBomTreeItemModel> = useMemo(
		() => flattenItems.reduce((acc, item) => ({ ...acc, [item.id]: item }), {}),
		[flattenItems]
	);

	const [expandedItems, setExpandedItems] = useState<string[]>(flattenItems.map((item) => item.id));

	return (
		<AavoTreeView<PartRevisionBomTreeItemModel>
			items={rootItems}
			slots={{
				item: CustomTreeItemWithStyles
			}}
			expandedItems={expandedItems}
			onExpandedItemsChange={(itemsIds) => {
				setExpandedItems(itemsIds);
			}}
			selectedItems={selectedItem?.id ?? null}
			onSelectedItemsChange={(_, itemId) => {
				const item = itemId != null ? allItemsByIds[itemId] : null;
				setSelectedItemLocal(item ?? null);
				onSelectionChanged?.(item ?? null);
			}}
			sx={{
				borderBottom: "1px solid",
				borderColor: "divider"
			}}
			actionBarComponents={
				<>
					<AavoTextField
						label={i18n.t("search")}
						onSubmit={onSearchQueryChange}
					/>
					{onShowRevisionsChange != null && (
						<AavoCheckbox
							label={i18n.t("show_revisions")}
							checked={showRevisions ?? false}
							onChange={onShowRevisionsChange}
						/>
					)}
				</>
			}
		/>
	);
};

const CustomTreeItem = React.memo((props: TreeItemProps) => {
	const { itemId } = props;
	const { publicAPI } = useTreeItem(props);
	const item = publicAPI.getItem(itemId) as PartRevisionBomTreeItemModel;

	return (
		<TreeItem
			{...props}
			slots={{
				label: CustomTreeItemLabel,
				expandIcon: CustomCollapseExpandIcon,
				collapseIcon: CustomCollapseExpandIcon
			}}
			slotProps={{
				label: {
					item: item
				} as any,
				content: {
					item: item
				} as any,
				expandIcon: {
					icon: faAngleRight
				} as any,
				collapseIcon: {
					icon: faAngleDown
				} as any
			}}
		/>
	);
});

const CustomTreeItemWithStyles = styled(CustomTreeItem)(() => ({
	"& > .MuiTreeItem-content": {
		paddingTop: "0.5rem",
		paddingBottom: "0.5rem"
	},
	"& .MuiTreeItem-iconContainer": {
		width: "2rem",
		"& svg": {
			fontSize: "1.2rem"
		}
	}
}));

const CustomTreeItemLabel = ({ item }: { item: PartRevisionBomTreeItemModel }) => {
	const theme = useTheme();
	return (
		<HorizontalBox
			sx={{
				alignItems: "center",
				gap: 1,
				backgroundColor: item.highlight ? alpha(theme.palette.secondary.light, 0.4) : undefined,
				paddingY: 0.25,
				paddingX: 1,
				borderRadius: 1.5
			}}
		>
			<Typography>{item.label}</Typography>
		</HorizontalBox>
	);
};

const CustomCollapseExpandIcon = ({ icon }: { icon: IconDefinition }) => {
	return (
		<HorizontalBox
			sx={{
				marginTop: -1.75,
				marginBottom: -1.75,
				paddingX: 1,
				alignItems: "center"
			}}
		>
			<FontAwesomeIcon icon={icon} />
		</HorizontalBox>
	);
};

